import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Fab, IconButton, Paper} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import AgentChatPanel, {AgentClient, AgentTurn} from '../../../../common/agent/AgentChatPanel';
import {AgentWorkflowMetadata, SafetyViewAssistConfig, SelectedNodeContext} from '../../../../../@types/agent';
import {InvestigationDTO} from '../../../../../@types/agent-investigation';
import {useWorkspaceContext} from '../../../../../context/workspaceContext';
import agentService from '../../../../../services/agent.service';
import ProposedChanges, {ProposalState} from './ProposedChanges';

/** A turn of the assistant, with what became of any proposals it made. */
export interface AssistantTurn extends AgentTurn<AgentWorkflowMetadata> {
    decided?: Record<string, ProposalState>;
}

const TURNS_KEY = 'isam-safety-view-assistant-turns';
const SESSION_KEY = 'isam-safety-view-assistant-session';

const SUGGESTED_PROMPTS = [
    'Is the statement of truth on the selected node any good?',
    'Run a consistency check on this view.',
    'Which barriers here have no failure criteria?',
    'Summarise the structure of this view.',
];

const newSessionId = (): string =>
    (window.crypto && window.crypto.randomUUID) ? window.crypto.randomUUID() : `${Date.now()}-${Math.random()}`;

const readStored = <T,>(key: string, fallback: T): T => {
    try {
        const raw = localStorage.getItem(key);
        return raw ? JSON.parse(raw) as T : fallback;
    } catch {
        return fallback;
    }
};

const writeStored = (key: string, value: unknown): void => {
    try {
        localStorage.setItem(key, JSON.stringify(value));
    } catch {
        // Storage can be full or blocked; the conversation still works for this sitting.
    }
};

/**
 * The conversation and session for one safety view, remembered in this browser.
 *
 * <p>Only the turns are kept here. The model's memory of the conversation is the server's, under the
 * session id, which is also what ties a proposal to the reader who may accept it.</p>
 */
export const useAssistantConversation = (eventSequenceId: number): {
    turns: AssistantTurn[];
    setTurns: (turns: AssistantTurn[]) => void;
    sessionId: string;
    newSession: () => void;
} => {
    const turnsKey = `${TURNS_KEY}-${eventSequenceId}`;
    const sessionKey = `${SESSION_KEY}-${eventSequenceId}`;
    const [turns, setTurnsState] = useState<AssistantTurn[]>(() => readStored<AssistantTurn[]>(turnsKey, []));
    const [sessionId, setSessionId] = useState<string>(() => readStored<string | null>(sessionKey, null) ?? newSessionId());

    useEffect((): void => {
        setTurnsState(readStored<AssistantTurn[]>(turnsKey, []));
        setSessionId(readStored<string | null>(sessionKey, null) ?? newSessionId());
    }, [turnsKey, sessionKey]);

    useEffect((): void => {
        writeStored(sessionKey, sessionId);
    }, [sessionKey, sessionId]);

    const setTurns = useCallback((next: AssistantTurn[]): void => {
        setTurnsState(next);
        writeStored(turnsKey, next);
    }, [turnsKey]);

    // A cleared conversation is a new one; the server's memory of the old one is left behind.
    const newSession = useCallback((): void => setSessionId(newSessionId()), []);

    return {turns, setTurns, sessionId, newSession};
};

const selectionContext = (node: {id?: number; uniqueId?: string; name?: string; nodeClass?: string; type?: string;
    statementOfTruth?: string} | null): SelectedNodeContext | undefined => {
    if (!node || node.id === undefined || node.id === null) {
        return undefined;
    }
    return {
        id: node.id,
        uniqueId: node.uniqueId ?? '',
        name: node.name ?? '',
        nodeClass: node.nodeClass ?? null,
        type: node.type ?? null,
        statementOfTruth: node.statementOfTruth ?? null,
    };
};

/**
 * The node a link in an answer points at, as a canvas cell id, or null when the target does not
 * name one. The agent is asked to put the node's id in the target ({@code #node-4471}); anything
 * else falls back to searching the canvas for the link's text, which is what the older chat panel
 * has always done and is all an answer that gave only a name can support.
 */
export const cellIdForNodeLink = (href: string): string | null => {
    const id = /^#node-(\d+)$/.exec(href.trim());
    return id ? `ft-${id[1]}` : null;
};

/** A link's text without any leading uniqueId, so a name search has a name to search for. */
export const nodeNameInLinkText = (text: string): string => text.replace(/^[\d.]+\s+/, '').trim();

interface SafetyViewAssistantProps {
    eventSequenceId: number;
    eventSequenceName: string;
}

/**
 * The assistant for the safety view open in the editor: a button bottom right that opens a panel in
 * which each question's work is shown as it happens. Each turn goes to the agent with the view and
 * the selected node; proposals come back under the answer as cards.
 */
const SafetyViewAssistant = ({eventSequenceId, eventSequenceName}: SafetyViewAssistantProps): React.ReactElement => {
    const {currentCanvasSelectedNode, canvasSearchFunction, canvasNavigateFunction} = useWorkspaceContext();
    const [open, setOpen] = useState<boolean>(false);
    const {turns, setTurns, sessionId, newSession} = useAssistantConversation(eventSequenceId);

    const client = useMemo((): AgentClient<AgentWorkflowMetadata> => ({
        ask: (question: string): Promise<InvestigationDTO<AgentWorkflowMetadata>> => {
            const actionConfig: SafetyViewAssistConfig = {eventSequenceId, eventSequenceName};
            const selection = selectionContext(currentCanvasSelectedNode as Parameters<typeof selectionContext>[0]);
            if (selection) {
                actionConfig.selectedNode = selection;
            }
            return agentService.ask<AgentWorkflowMetadata>({
                action: 'SAFETY_VIEW_ASSIST', prompt: question, sessionId, actionConfig,
            });
        },
        progress: (id: string): Promise<InvestigationDTO<AgentWorkflowMetadata>> =>
            agentService.progress<AgentWorkflowMetadata>(id),
    }), [eventSequenceId, eventSequenceName, sessionId, currentCanvasSelectedNode]);

    /** Takes the canvas to the node an answer linked to, rather than letting the browser follow it. */
    const navigateToLinkedNode = useCallback((href: string, text: string): void => {
        if (!canvasNavigateFunction) {
            return;
        }
        const cellId = cellIdForNodeLink(href);
        if (cellId) {
            canvasNavigateFunction(cellId);
            return;
        }
        const results = canvasSearchFunction ? canvasSearchFunction(nodeNameInLinkText(text)) : [];
        if (results.length > 0) {
            canvasNavigateFunction(results[0].id);
        }
    }, [canvasNavigateFunction, canvasSearchFunction]);

    /**
     * The way out of an answer that recommends changes without offering them.
     *
     * <p>The assistant is told to propose what it recommends, but a turn that spends its budget
     * surveying still ends in advice the analyst would have to retype. Asking in the same session
     * costs one more turn and keeps everything it has already looked up. Offered only when the
     * backend says the turn left changes unproposed, so an answer with nothing to change has no
     * button.</p>
     */
    const followUp = useCallback((investigation: InvestigationDTO<AgentWorkflowMetadata>):
        {label: string; question: string} | null => {
        if (!investigation.answer || !investigation.metadata?.unproposedChanges) {
            return null;
        }
        const noted = investigation.metadata.unproposedChangeList ?? [];
        const which = noted.length > 0
            ? `these changes you recommended: ${noted.join('; ')}.`
            : 'each change you just recommended and have not already proposed.';
        return {
            label: 'Propose these changes',
            question: `Propose ${which} One call per change, with the complete new value. If one cannot be `
                + 'proposed, say so in one sentence instead.',
        };
    }, []);

    const renderAnswerExtras = useCallback((
        investigation: InvestigationDTO<AgentWorkflowMetadata>,
        turnIndex: number,
    ): React.ReactNode => {
        const proposals = investigation.metadata?.proposals ?? [];
        if (proposals.length === 0) {
            return null;
        }
        return (
            <ProposedChanges
                payload={{
                    sessionId,
                    proposals,
                    decided: turns[turnIndex]?.decided,
                    onDecided: (decisions: Record<string, ProposalState>): void => {
                        setTurns(turns.map((held: AssistantTurn, index: number) => index === turnIndex
                            ? {...held, decided: {...(held.decided ?? {}), ...decisions}}
                            : held));
                    },
                }}
            />
        );
    }, [sessionId, setTurns, turns]);

    return (
        <>
            {!open && (
                <Fab
                    variant="extended"
                    color="primary"
                    size="medium"
                    aria-label="Ask about this view"
                    onClick={(): void => setOpen(true)}
                    sx={{position: 'absolute', right: 24, bottom: 24, zIndex: 4}}
                >
                    <AutoAwesomeIcon sx={{mr: 1}} />
                    Ask about this view
                </Fab>
            )}

            {/* Kept mounted while hidden so an answer in progress still lands. */}
            <Paper
                elevation={8}
                role="dialog"
                aria-label="Safety view assistant"
                data-testid="safety-view-assistant-panel"
                sx={{
                    display: open ? 'flex' : 'none',
                    flexDirection: 'column',
                    position: 'absolute',
                    right: 24,
                    bottom: 24,
                    width: 'min(520px, calc(100% - 48px))',
                    height: 'min(680px, calc(100% - 48px))',
                    zIndex: 5,
                    overflow: 'hidden',
                }}
            >
                <AgentChatPanel<AgentWorkflowMetadata>
                    title={`Ask about ${eventSequenceName}`}
                    intro="Questions are answered by looking the view up, node by node, with ISAM's own checks. You will see each lookup as it runs. Changes are offered as proposals for you to accept or reject."
                    placeholder="Ask about this safety view"
                    suggestedPrompts={SUGGESTED_PROMPTS}
                    turns={turns}
                    onTurnsChange={setTurns}
                    onCleared={newSession}
                    client={client}
                    renderAnswerExtras={renderAnswerExtras}
                    onAnswerLink={navigateToLinkedNode}
                    followUp={followUp}
                    headerActions={
                        <IconButton size="small" aria-label="Close the assistant" onClick={(): void => setOpen(false)}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    }
                />
            </Paper>
        </>
    );
};

export default SafetyViewAssistant;
